import { api } from '../../core/api.js';
import { clearDraft, payloadFromState, stateFromProduct, wizardPath } from './model.js';

/**
 * @param {object} state
 * @returns {Promise<{ ok: boolean, error?: string, product?: object }>}
 */
export async function saveProduct(state) {
  const built = payloadFromState(state);
  if (built.error) return { ok: false, error: built.error };
  const editing = built.editing;
  const result = editing
    ? await api(`/products/${encodeURIComponent(state.productId)}`, {
      method: 'PUT',
      body: built.body,
    })
    : await api('/products', { method: 'POST', body: built.body });
  if (!result || result.error || !result.product || !result.product.id) {
    return { ok: false, error: (result && result.error) || 'Could not save the product' };
  }
  return { ok: true, product: result.product, editing };
}

/**
 * @param {string} productId
 * @returns {Promise<{ ok: boolean, error?: string, state?: object }>}
 */
export async function loadProduct(productId) {
  const detail = await api(`/products/${encodeURIComponent(productId)}`);
  if (!detail || detail.error || !detail.product) {
    return { ok: false, error: (detail && detail.error) || 'Could not load that product' };
  }
  const docs = await api(`/documents?product_id=${encodeURIComponent(productId)}`);
  const list = docs && !docs.error ? (docs.documents || []) : [];
  return { ok: true, state: stateFromProduct(detail.product, list) };
}

/**
 * Pin product + step (+ optional in-flow connect source) into the URL.
 * @param {string} productId
 * @param {number} step
 * @param {string} [connectSource]
 */
export function syncWizardUrl(productId, step, connectSource) {
  const next = wizardPath(productId, step, connectSource || '');
  const cur = `${window.location.pathname}${window.location.search}`;
  if (cur === next) return;
  try { window.history.replaceState(null, '', next); } catch { /* ignore */ }
}

/**
 * Apply a saved product onto wizard state and clear the create draft.
 * @param {object} state
 * @param {object} product
 */
export function acceptSaved(state, product) {
  state.productId = product.id;
  state.saved = {
    icon: product.icon,
    color: product.color,
    category: product.category,
    slug: product.slug,
    status: product.status,
  };
  clearDraft();
}

export function refreshShellProducts() {
  try { document.dispatchEvent(new CustomEvent('bonnie:products')); } catch { /* ignore */ }
}

/**
 * @param {string} productId
 */
export async function loadConnectorBundle(productId) {
  const q = productId ? `?product_id=${encodeURIComponent(productId)}` : '';
  const [integrations, providers, depth, mcp] = await Promise.all([
    productId ? api(`/connectors${q}`) : Promise.resolve({ integrations: [] }),
    api('/connectors/providers'),
    productId ? api(`/connectors/depth${q}`) : Promise.resolve({ analysis_depth: 0, depth_breakdown: {} }),
    productId ? api(`/mcp/connect${q}`) : Promise.resolve({ servers: [] }),
  ]);
  return { integrations, providers, depth, mcp };
}
